import { ChevronDown, Plus, X } from "lucide-react";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { IntegrationStatus, Product } from "@outpost/shared";
import {
  GoogleConnectWizard,
  type PropertyRow,
  type SiteRow,
} from "./GoogleConnectWizard";

export type IntegrationCard = {
  id: string | null;
  organizationId: string;
  productId: string | null;
  provider: string;
  label: string;
  status: IntegrationStatus;
  lastSyncedAt: string | null;
  metaJson: string | null;
  createdAt: string | null;
  live?: boolean;
  credentialsConfigured?: boolean;
};

export const CONNECTION_CATALOG: {
  provider: string;
  label: string;
  blurb: string;
  live: boolean;
  xpHint: string;
  logo: string;
}[] = [
  {
    provider: "buffer",
    label: "Buffer",
    blurb: "Social publish jobs — evidence for the ops map.",
    live: true,
    xpHint: "Lights the social lookout",
    logo: "/brand/integrations/buffer.svg",
  },
  {
    provider: "ga",
    label: "Google Analytics",
    blurb: "GA4 sessions — map one or more properties to products.",
    live: true,
    xpHint: "Lights traffic & search lookouts",
    logo: "/brand/integrations/ga.svg",
  },
  {
    provider: "gsc",
    label: "Search Console",
    blurb: "Search clicks — same Google OAuth; map verified sites to products.",
    live: true,
    xpHint: "Lights the search lookout",
    logo: "/brand/integrations/gsc.svg",
  },
  {
    provider: "posthog",
    label: "PostHog",
    blurb: "Per-product activation — signup, first value, sessions, errors.",
    live: true,
    xpHint: "Lights the activation lookout",
    logo: "/brand/integrations/posthog.svg",
  },
  {
    provider: "stripe",
    label: "Stripe",
    blurb: "Paying customers — billing truth.",
    live: false,
    xpHint: "Coming soon",
    logo: "/brand/integrations/stripe.svg",
  },
  {
    provider: "instantly",
    label: "Instantly",
    blurb: "Outbound + warmup health.",
    live: true,
    xpHint: "Lights the outbound lookout",
    logo: "/brand/integrations/instantly.svg",
  },
  {
    provider: "ahrefs",
    label: "Ahrefs",
    blurb: "Domain Rating for all product sites — one platform key.",
    live: true,
    xpHint: "Lights the SEO lookout",
    logo: "/brand/integrations/ahrefs.svg",
  },
];

function ConnectorMark({
  provider,
  label,
  size = 28,
}: {
  provider: string;
  label: string;
  size?: number;
}) {
  const logo =
    CONNECTION_CATALOG.find((c) => c.provider === provider)?.logo ??
    "/brand/outpost16-lookout-app-mark.png";
  return (
    <span className="op-conn-mark" style={{ width: size, height: size }}>
      <img src={logo} alt="" width={size} height={size} />
      <span className="sr-only">{label}</span>
    </span>
  );
}

function statusLabel(status: IntegrationStatus, ic?: IntegrationCard): string {
  switch (status) {
    case "connected":
      return "Connected";
    case "stale":
      return "Stale";
    case "error":
      return "Needs attention";
    case "disconnected":
    default:
      if (ic?.credentialsConfigured && (ic.provider === "ga" || ic.provider === "gsc")) {
        return "OAuth ready — map sites";
      }
      return "Disconnected";
  }
}

function isActiveModule(ic: IntegrationCard): boolean {
  if (
    ic.status === "connected" ||
    ic.status === "stale" ||
    ic.status === "error"
  ) {
    return true;
  }
  // Google OAuth vaults both GA + GSC secrets; show GSC so sites can be mapped.
  if (
    (ic.provider === "ga" || ic.provider === "gsc") &&
    ic.credentialsConfigured
  ) {
    return true;
  }
  return false;
}

type BufferChannelStatsView = {
  channelId: string;
  name: string;
  service: string | null;
  scheduledCount?: number | null;
  published7d?: number | null;
  clicks7d?: number | null;
  impressions7d?: number | null;
  comments7d?: number | null;
  reactions7d?: number | null;
  reposts7d?: number | null;
  shares7d?: number | null;
};

type BufferStatsView = {
  scheduledCount?: number;
  scheduledTruncated?: boolean;
  published7d?: number;
  published7dTruncated?: boolean;
  errorCount?: number;
  pausedChannelCount?: number;
  channelCount?: number;
  nextDueAt?: string | null;
  dailySent?: number;
  dailyScheduled?: number;
  dailyLimit?: number | null;
  dailyAtLimit?: boolean;
  clicks7d?: number | null;
  impressions7d?: number | null;
  comments7d?: number | null;
  reactions7d?: number | null;
  reposts7d?: number | null;
  shares7d?: number | null;
  postCount7d?: number | null;
  impressions7dPrior?: number | null;
  clicks7dPrior?: number | null;
  postCount7dPrior?: number | null;
  comments7dPrior?: number | null;
  reactions7dPrior?: number | null;
  reposts7dPrior?: number | null;
  shares7dPrior?: number | null;
  published1d?: number | null;
  impressions1d?: number | null;
  clicks1d?: number | null;
  comments1d?: number | null;
  reactions1d?: number | null;
  reposts1d?: number | null;
  shares1d?: number | null;
  postCount1d?: number | null;
  published1dPrior?: number | null;
  impressions1dPrior?: number | null;
  clicks1dPrior?: number | null;
  comments1dPrior?: number | null;
  reactions1dPrior?: number | null;
  reposts1dPrior?: number | null;
  shares1dPrior?: number | null;
  postCount1dPrior?: number | null;
  published30d?: number | null;
  impressions30d?: number | null;
  clicks30d?: number | null;
  comments30d?: number | null;
  reactions30d?: number | null;
  reposts30d?: number | null;
  shares30d?: number | null;
  postCount30d?: number | null;
  published30dPrior?: number | null;
  impressions30dPrior?: number | null;
  clicks30dPrior?: number | null;
  comments30dPrior?: number | null;
  reactions30dPrior?: number | null;
  reposts30dPrior?: number | null;
  shares30dPrior?: number | null;
  postCount30dPrior?: number | null;
  window?: { start?: string; end?: string };
  window1d?: { start?: string; end?: string } | null;
  window30d?: { start?: string; end?: string } | null;
  channels?: { id?: string; name: string; service: string | null; isQueuePaused: boolean }[];
  channelStats?: BufferChannelStatsView[];
};

export function parseBufferStats(metaJson: string | null): BufferStatsView | null {
  if (!metaJson) return null;
  try {
    const meta = JSON.parse(metaJson) as { stats?: BufferStatsView };
    return meta.stats ?? null;
  } catch {
    return null;
  }
}

type InstantlyWarmupAccountView = {
  email: string;
  healthScore: number | null;
  healthLabel: string | null;
  sent: number;
  landedInbox: number;
  landedSpam: number;
  received: number;
};

type InstantlyWarmupView = {
  accounts: InstantlyWarmupAccountView[];
  avgHealthScore: number | null;
  inboxRate: number | null;
  spamRate: number | null;
  series: Array<{ at: string; inboxRate: number }>;
};

type InstantlyStatsView = {
  sent7d?: number;
  opened7d?: number;
  clicked7d?: number;
  replied7d?: number;
  campaignCount?: number;
  accountCount?: number;
  window?: { start?: string; end?: string };
  campaigns?: {
    campaignId: string;
    campaignName: string;
    sent: number;
    opened: number;
    clicked: number;
    replied: number;
  }[];
  warmup?: InstantlyWarmupView | null;
  warmupError?: string | null;
};

export function parseInstantlyStats(
  metaJson: string | null,
): InstantlyStatsView | null {
  if (!metaJson) return null;
  try {
    const meta = JSON.parse(metaJson) as {
      stats?: InstantlyStatsView;
      accountCount?: number;
      warmup?: InstantlyWarmupView | null;
      warmupError?: string | null;
    };
    const stats = meta.stats;
    if (!stats && meta.warmup == null && meta.accountCount == null) return null;
    return {
      ...(stats ?? {}),
      accountCount: stats?.accountCount ?? meta.accountCount,
      warmup: meta.warmup ?? stats?.warmup ?? null,
      warmupError: meta.warmupError ?? stats?.warmupError ?? null,
    };
  } catch {
    return null;
  }
}

export type InstantlyGrowthView = {
  scopeLabel: string;
  mode: "warmup" | "campaign";
  unmatched: boolean;
  accountCount: number;
  sent7d: number | null;
  opened7d: number | null;
  clicked7d: number | null;
  replied7d: number | null;
  avgHealthScore: number | null;
  inboxRate: number | null;
  spamLandings: number | null;
  series: Array<{ at: string; value: number }>;
  warmupError: string | null;
};

/** Org-level Instantly rollup (product maps later). */
export function instantlyGrowthForProductScope(
  stats: InstantlyStatsView,
  _product: { name: string; slug: string } | null,
): InstantlyGrowthView {
  const sent7d = typeof stats.sent7d === "number" ? stats.sent7d : null;
  const accountCount =
    typeof stats.accountCount === "number"
      ? stats.accountCount
      : (stats.warmup?.accounts?.length ?? 0);
  const warmup = stats.warmup ?? null;
  const mode: "warmup" | "campaign" =
    (sent7d === 0 || sent7d == null) && accountCount > 0
      ? "warmup"
      : "campaign";
  const spamLandings = warmup
    ? warmup.accounts.reduce((s, a) => s + (a.landedSpam ?? 0), 0)
    : null;

  return {
    scopeLabel: "All accounts",
    mode,
    unmatched: false,
    accountCount,
    sent7d,
    opened7d: typeof stats.opened7d === "number" ? stats.opened7d : null,
    clicked7d: typeof stats.clicked7d === "number" ? stats.clicked7d : null,
    replied7d: typeof stats.replied7d === "number" ? stats.replied7d : null,
    avgHealthScore: warmup?.avgHealthScore ?? null,
    inboxRate: warmup?.inboxRate ?? null,
    spamLandings,
    series: (warmup?.series ?? []).map((p) => ({
      at: p.at,
      value: Math.round(p.inboxRate * 1000) / 10,
    })),
    warmupError: stats.warmupError ?? null,
  };
}

export type GaBreakdownRow = {
  name: string;
  value: number;
  valuePrior: number | null;
};

export type GaMetricSnapshot = {
  sessions: number | null;
  activeUsers: number | null;
  newUsers: number | null;
  avgEngagementSec: number | null;
  eventCount: number | null;
};

export type GaWindowBundle = {
  current: GaMetricSnapshot;
  prior: GaMetricSnapshot;
  byCountry: GaBreakdownRow[];
  byPage: GaBreakdownRow[];
  byChannel: GaBreakdownRow[];
};

export type GaPropertyStat = {
  propertyId: string;
  productId: string | null;
  label?: string;
  sessions7d: number | null;
  sessions7dPrior?: number | null;
  windows?: Partial<Record<"day" | "week" | "month", GaWindowBundle>>;
};

export type GscMetricSnapshot = {
  clicks: number | null;
  impressions: number | null;
  position: number | null;
  ctr: number | null;
};

export type GscWindowBundle = {
  current: GscMetricSnapshot;
  prior: GscMetricSnapshot;
};

export type GscIndexStatus = {
  inspectionUrl: string;
  lastCrawlTime: string | null;
  coverageState: string | null;
  verdict: string | null;
  sitemapIndexed: number | null;
  sitemapSubmitted: number | null;
  indexingReportUrl: string;
  notIndexedReportUrl: string;
};

export type GscSiteStat = {
  siteUrl: string;
  productId: string | null;
  label?: string;
  clicks7d: number | null;
  impressions7d: number | null;
  clicks7dPrior?: number | null;
  impressions7dPrior?: number | null;
  position7d?: number | null;
  position7dPrior?: number | null;
  windows?: Partial<Record<"day" | "week" | "month", GscWindowBundle>>;
  indexStatus?: GscIndexStatus | null;
};

export function parseGaMeta(metaJson: string | null): {
  sessions7d: number | null;
  sessions7dPrior: number | null;
  properties: GaPropertyStat[];
  propertyId?: string;
  productId?: string | null;
  windows?: Partial<Record<"day" | "week" | "month", GaWindowBundle>>;
} | null {
  if (!metaJson) return null;
  try {
    const meta = JSON.parse(metaJson) as {
      sessions7d?: number | null;
      sessions7dPrior?: number | null;
      propertyId?: string;
      productId?: string | null;
      properties?: GaPropertyStat[];
      windows?: Partial<Record<"day" | "week" | "month", GaWindowBundle>>;
    };
    const properties = Array.isArray(meta.properties)
      ? meta.properties.filter((p) => p?.propertyId)
      : meta.propertyId
        ? [
            {
              propertyId: meta.propertyId,
              productId: meta.productId ?? null,
              sessions7d: meta.sessions7d ?? null,
              sessions7dPrior: meta.sessions7dPrior ?? null,
              windows: meta.windows,
            },
          ]
        : [];
    if (!properties.length && meta.sessions7d == null) return null;
    return {
      sessions7d: meta.sessions7d ?? null,
      sessions7dPrior: meta.sessions7dPrior ?? null,
      properties,
      propertyId: meta.propertyId,
      productId: meta.productId,
      windows: meta.windows,
    };
  } catch {
    return null;
  }
}

export function parseGscMeta(metaJson: string | null): {
  clicks7d: number | null;
  impressions7d: number | null;
  clicks7dPrior: number | null;
  impressions7dPrior: number | null;
  position7d: number | null;
  position7dPrior: number | null;
  sites: GscSiteStat[];
  windows?: Partial<Record<"day" | "week" | "month", GscWindowBundle>>;
  indexStatus?: GscIndexStatus | null;
  siteUrl?: string;
  productId?: string | null;
  needsSite?: boolean;
  error?: string | null;
} | null {
  if (!metaJson) return null;
  try {
    const meta = JSON.parse(metaJson) as {
      clicks7d?: number | null;
      impressions7d?: number | null;
      clicks7dPrior?: number | null;
      impressions7dPrior?: number | null;
      position7d?: number | null;
      position7dPrior?: number | null;
      siteUrl?: string;
      productId?: string | null;
      needsSite?: boolean;
      error?: string;
      sites?: GscSiteStat[];
      windows?: Partial<Record<"day" | "week" | "month", GscWindowBundle>>;
      indexStatus?: GscIndexStatus | null;
    };
    const sites = Array.isArray(meta.sites)
      ? meta.sites.filter((s) => s?.siteUrl)
      : meta.siteUrl
        ? [
            {
              siteUrl: meta.siteUrl,
              productId: meta.productId ?? null,
              clicks7d: meta.clicks7d ?? null,
              impressions7d: meta.impressions7d ?? null,
              clicks7dPrior: meta.clicks7dPrior ?? null,
              impressions7dPrior: meta.impressions7dPrior ?? null,
              position7d: meta.position7d ?? null,
              position7dPrior: meta.position7dPrior ?? null,
              windows: meta.windows,
              indexStatus: meta.indexStatus ?? null,
            },
          ]
        : [];
    return {
      clicks7d: meta.clicks7d ?? null,
      impressions7d: meta.impressions7d ?? null,
      clicks7dPrior: meta.clicks7dPrior ?? null,
      impressions7dPrior: meta.impressions7dPrior ?? null,
      position7d: meta.position7d ?? null,
      position7dPrior: meta.position7dPrior ?? null,
      sites,
      windows: meta.windows,
      indexStatus: meta.indexStatus ?? null,
      siteUrl: meta.siteUrl,
      productId: meta.productId,
      needsSite: meta.needsSite,
      error: typeof meta.error === "string" ? meta.error : null,
    };
  } catch {
    return null;
  }
}

export type GoogleGrowthView = {
  scopeLabel: string;
  sessions7d: number | null;
  sessions7dPrior: number | null;
  propertyCount: number;
  unmatched: boolean;
  windowId: "day" | "week" | "month";
  windowLabel: string;
  activeUsers: number | null;
  activeUsersPrior: number | null;
  newUsers: number | null;
  newUsersPrior: number | null;
  avgEngagementSec: number | null;
  avgEngagementSecPrior: number | null;
  eventCount: number | null;
  eventCountPrior: number | null;
  byCountry: GaBreakdownRow[];
  byPage: GaBreakdownRow[];
  byChannel: GaBreakdownRow[];
};

export type GscGrowthView = {
  scopeLabel: string;
  unmatched: boolean;
  windowId: "day" | "week" | "month";
  windowLabel: string;
  siteCount: number;
  clicks: number | null;
  clicksPrior: number | null;
  impressions: number | null;
  impressionsPrior: number | null;
  position: number | null;
  positionPrior: number | null;
  indexStatus: GscIndexStatus | null;
  /** Primary site URL for deep links. */
  siteUrl: string | null;
};

function emptyGaSnapshot(): GaMetricSnapshot {
  return {
    sessions: null,
    activeUsers: null,
    newUsers: null,
    avgEngagementSec: null,
    eventCount: null,
  };
}

function sumGaSnapshots(list: GaMetricSnapshot[]): GaMetricSnapshot {
  const empty = emptyGaSnapshot();
  if (!list.length) return empty;
  let sessions: number | null = null;
  let activeUsers: number | null = null;
  let newUsers: number | null = null;
  let eventCount: number | null = null;
  let engagementTotal: number | null = null;
  for (const s of list) {
    if (s.sessions != null) sessions = (sessions ?? 0) + s.sessions;
    if (s.activeUsers != null) activeUsers = (activeUsers ?? 0) + s.activeUsers;
    if (s.newUsers != null) newUsers = (newUsers ?? 0) + s.newUsers;
    if (s.eventCount != null) eventCount = (eventCount ?? 0) + s.eventCount;
    if (s.avgEngagementSec != null && s.activeUsers != null) {
      engagementTotal =
        (engagementTotal ?? 0) + s.avgEngagementSec * s.activeUsers;
    }
  }
  return {
    sessions,
    activeUsers,
    newUsers,
    eventCount,
    avgEngagementSec:
      engagementTotal != null && activeUsers != null && activeUsers > 0
        ? Math.round(engagementTotal / activeUsers)
        : null,
  };
}

function mergeGaBreakdowns(lists: GaBreakdownRow[][]): GaBreakdownRow[] {
  const map = new Map<string, GaBreakdownRow>();
  for (const list of lists) {
    for (const row of list) {
      const cur = map.get(row.name);
      if (!cur) map.set(row.name, { ...row });
      else {
        cur.value += row.value;
        if (row.valuePrior != null) {
          cur.valuePrior = (cur.valuePrior ?? 0) + row.valuePrior;
        }
      }
    }
  }
  return [...map.values()].sort((a, b) => b.value - a.value).slice(0, 7);
}

function emptyGscSnapshot(): GscMetricSnapshot {
  return {
    clicks: null,
    impressions: null,
    position: null,
    ctr: null,
  };
}

function sumGscSnapshots(list: GscMetricSnapshot[]): GscMetricSnapshot {
  if (!list.length) return emptyGscSnapshot();
  let clicks: number | null = null;
  let impressions: number | null = null;
  let weightedPos = 0;
  let weight = 0;
  for (const s of list) {
    if (s.clicks != null) clicks = (clicks ?? 0) + s.clicks;
    if (s.impressions != null) {
      impressions = (impressions ?? 0) + s.impressions;
      if (s.position != null && s.impressions > 0) {
        weightedPos += s.position * s.impressions;
        weight += s.impressions;
      }
    }
  }
  return {
    clicks,
    impressions,
    position: weight > 0 ? Math.round((weightedPos / weight) * 10) / 10 : null,
    ctr:
      clicks != null && impressions != null && impressions > 0
        ? Math.round((clicks / impressions) * 1000) / 10
        : null,
  };
}

export function googleGrowthForProductScope(
  ga: ReturnType<typeof parseGaMeta>,
  product: { id: string; name: string } | null,
  windowId: "day" | "week" | "month" = "week",
): GoogleGrowthView | null {
  if (!ga) return null;
  const windowLabel =
    windowId === "day" ? "day" : windowId === "week" ? "7 days" : "30 days";

  const props = product
    ? (ga.properties ?? []).filter((p) => p.productId === product.id)
    : (ga.properties ?? []);

  if (product && props.length === 0) {
    return {
      scopeLabel: product.name,
      sessions7d: null,
      sessions7dPrior: null,
      propertyCount: 0,
      unmatched: true,
      windowId,
      windowLabel,
      activeUsers: null,
      activeUsersPrior: null,
      newUsers: null,
      newUsersPrior: null,
      avgEngagementSec: null,
      avgEngagementSecPrior: null,
      eventCount: null,
      eventCountPrior: null,
      byCountry: [],
      byPage: [],
      byChannel: [],
    };
  }

  const windowBundles = props
    .map((p) => p.windows?.[windowId])
    .filter((b): b is GaWindowBundle => Boolean(b));
  const rolled =
    !product && ga.windows?.[windowId]
      ? ga.windows[windowId]
      : windowBundles.length
        ? {
            current: sumGaSnapshots(windowBundles.map((b) => b.current)),
            prior: sumGaSnapshots(windowBundles.map((b) => b.prior)),
            byCountry: mergeGaBreakdowns(windowBundles.map((b) => b.byCountry)),
            byPage: mergeGaBreakdowns(windowBundles.map((b) => b.byPage)),
            byChannel: mergeGaBreakdowns(
              windowBundles.map((b) => b.byChannel),
            ),
          }
        : null;

  // Legacy fallback when windows haven't been synced yet.
  const sessions = rolled?.current.sessions ??
    (props.length
      ? props.reduce((sum, p) => sum + (p.sessions7d ?? 0), 0)
      : (ga.sessions7d ?? null));
  const sessionsPrior = rolled?.prior.sessions ??
    (props.length
      ? props.some((p) => p.sessions7dPrior != null)
        ? props.reduce((sum, p) => sum + (p.sessions7dPrior ?? 0), 0)
        : null
      : (ga.sessions7dPrior ?? null));

  const weekBundles = props
    .map((p) => p.windows?.week)
    .filter((b): b is GaWindowBundle => Boolean(b));
  const weekRolled =
    !product && ga.windows?.week
      ? ga.windows.week
      : weekBundles.length
        ? {
            byCountry: mergeGaBreakdowns(weekBundles.map((b) => b.byCountry)),
            byPage: mergeGaBreakdowns(weekBundles.map((b) => b.byPage)),
            byChannel: mergeGaBreakdowns(
              weekBundles.map((b) => b.byChannel),
            ),
          }
        : null;

  return {
    scopeLabel: product?.name ?? "All products",
    sessions7d: sessions,
    sessions7dPrior: sessionsPrior,
    propertyCount: props.length || (ga.properties.length ?? 0),
    unmatched: false,
    windowId,
    windowLabel,
    activeUsers: rolled?.current.activeUsers ?? null,
    activeUsersPrior: rolled?.prior.activeUsers ?? null,
    newUsers: rolled?.current.newUsers ?? null,
    newUsersPrior: rolled?.prior.newUsers ?? null,
    avgEngagementSec: rolled?.current.avgEngagementSec ?? null,
    avgEngagementSecPrior: rolled?.prior.avgEngagementSec ?? null,
    eventCount: rolled?.current.eventCount ?? null,
    eventCountPrior: rolled?.prior.eventCount ?? null,
    byCountry: (rolled?.byCountry?.length ? rolled.byCountry : null) ??
      weekRolled?.byCountry ??
      [],
    byPage: (rolled?.byPage?.length ? rolled.byPage : null) ??
      weekRolled?.byPage ??
      [],
    byChannel: (rolled?.byChannel?.length ? rolled.byChannel : null) ??
      weekRolled?.byChannel ??
      [],
  };
}

export function gscGrowthForProductScope(
  gsc: ReturnType<typeof parseGscMeta>,
  product: { id: string; name: string } | null,
  windowId: "day" | "week" | "month" = "week",
): GscGrowthView | null {
  if (!gsc) return null;
  const windowLabel =
    windowId === "day" ? "day" : windowId === "week" ? "7 days" : "30 days";

  const sites = product
    ? (gsc.sites ?? []).filter((s) => s.productId === product.id)
    : (gsc.sites ?? []);

  if (product && sites.length === 0) {
    return {
      scopeLabel: product.name,
      unmatched: true,
      windowId,
      windowLabel,
      siteCount: 0,
      clicks: null,
      clicksPrior: null,
      impressions: null,
      impressionsPrior: null,
      position: null,
      positionPrior: null,
      indexStatus: null,
      siteUrl: null,
    };
  }

  const windowBundles = sites
    .map((s) => s.windows?.[windowId])
    .filter((b): b is GscWindowBundle => Boolean(b));
  const rolled =
    !product && gsc.windows?.[windowId]
      ? gsc.windows[windowId]
      : windowBundles.length
        ? {
            current: sumGscSnapshots(windowBundles.map((b) => b.current)),
            prior: sumGscSnapshots(windowBundles.map((b) => b.prior)),
          }
        : null;

  const clicks =
    rolled?.current.clicks ??
    (sites.length
      ? sites.reduce((sum, s) => sum + (s.clicks7d ?? 0), 0)
      : (gsc.clicks7d ?? null));
  const clicksPrior =
    rolled?.prior.clicks ??
    (sites.length
      ? sites.some((s) => s.clicks7dPrior != null)
        ? sites.reduce((sum, s) => sum + (s.clicks7dPrior ?? 0), 0)
        : null
      : (gsc.clicks7dPrior ?? null));
  const impressions =
    rolled?.current.impressions ??
    (sites.length
      ? sites.reduce((sum, s) => sum + (s.impressions7d ?? 0), 0)
      : (gsc.impressions7d ?? null));
  const impressionsPrior =
    rolled?.prior.impressions ??
    (sites.length
      ? sites.some((s) => s.impressions7dPrior != null)
        ? sites.reduce((sum, s) => sum + (s.impressions7dPrior ?? 0), 0)
        : null
      : (gsc.impressions7dPrior ?? null));
  const position =
    rolled?.current.position ??
    (sites.length === 1
      ? (sites[0]?.position7d ?? null)
      : (gsc.position7d ?? null));
  const positionPrior =
    rolled?.prior.position ??
    (sites.length === 1
      ? (sites[0]?.position7dPrior ?? null)
      : (gsc.position7dPrior ?? null));

  const primary = sites[0] ?? null;
  const indexStatus =
    primary?.indexStatus ?? gsc.indexStatus ?? null;

  return {
    scopeLabel: product?.name ?? "All products",
    unmatched: false,
    windowId,
    windowLabel,
    siteCount: sites.length || (gsc.sites.length ?? 0),
    clicks,
    clicksPrior,
    impressions,
    impressionsPrior,
    position,
    positionPrior,
    indexStatus,
    siteUrl: primary?.siteUrl ?? gsc.siteUrl ?? null,
  };
}

export type AhrefsTargetStat = {
  target: string;
  productId: string | null;
  label?: string;
  domainRating: number | null;
  ahrefsRank: number | null;
  domainRating30d: number | null;
  domainRating90d: number | null;
  drDelta30d: number | null;
  drDelta90d: number | null;
  baselineDr?: number | null;
  baselineAt?: string | null;
  drDeltaSinceBaseline?: number | null;
  liveBacklinks: number | null;
  liveRefdomains: number | null;
  orgTraffic: number | null;
  orgKeywords: number | null;
  orgKeywordsTop3: number | null;
};

export function parseAhrefsMeta(metaJson: string | null): {
  targets: AhrefsTargetStat[];
  domainRatingAvg: number | null;
  liveRefdomainsTotal: number | null;
  orgTrafficTotal: number | null;
  apiMode?: "public" | "site-explorer";
  attribution?: string;
  drHistory?: Record<string, { at: string; domainRating: number }[]>;
} | null {
  if (!metaJson) return null;
  try {
    const meta = JSON.parse(metaJson) as {
      targets?: AhrefsTargetStat[];
      domainRatingAvg?: number | null;
      liveRefdomainsTotal?: number | null;
      orgTrafficTotal?: number | null;
      target?: string;
      productId?: string | null;
      domainRating?: number | null;
      apiMode?: "public" | "site-explorer";
      attribution?: string;
      drHistory?: Record<string, { at: string; domainRating: number }[]>;
    };
    const targets = Array.isArray(meta.targets)
      ? meta.targets.filter((t) => t?.target)
      : meta.target
        ? [
            {
              target: meta.target,
              productId: meta.productId ?? null,
              domainRating: meta.domainRating ?? null,
              ahrefsRank: null,
              domainRating30d: null,
              domainRating90d: null,
              drDelta30d: null,
              drDelta90d: null,
              liveBacklinks: null,
              liveRefdomains: null,
              orgTraffic: null,
              orgKeywords: null,
              orgKeywordsTop3: null,
            },
          ]
        : [];
    if (!targets.length) return null;
    return {
      targets,
      domainRatingAvg: meta.domainRatingAvg ?? null,
      liveRefdomainsTotal: meta.liveRefdomainsTotal ?? null,
      orgTrafficTotal: meta.orgTrafficTotal ?? null,
      apiMode: meta.apiMode,
      attribution: meta.attribution,
      drHistory: meta.drHistory,
    };
  } catch {
    return null;
  }
}

export type AhrefsGrowthView = {
  scopeLabel: string;
  domainRating: number | null;
  drDelta30d: number | null;
  drDelta90d: number | null;
  drDeltaSinceBaseline: number | null;
  baselineDr: number | null;
  liveRefdomains: number | null;
  orgTraffic: number | null;
  orgKeywords: number | null;
  orgKeywordsTop3: number | null;
  targetCount: number;
  unmatched: boolean;
  targets: AhrefsTargetStat[];
  apiMode?: "public" | "site-explorer";
  attribution?: string;
  /** DR sparkline points from Outpost-owned history. */
  series: Array<{ at: string; value: number }>;
};

function ahrefsSeriesForTargets(
  ahrefs: NonNullable<ReturnType<typeof parseAhrefsMeta>>,
  targets: AhrefsTargetStat[],
): Array<{ at: string; value: number }> {
  const hist = ahrefs.drHistory;
  if (!hist || !targets.length) return [];
  if (targets.length === 1) {
    const rows = hist[targets[0]!.target] ?? [];
    return rows
      .map((r) => ({ at: r.at, value: r.domainRating }))
      .sort((a, b) => a.at.localeCompare(b.at));
  }
  // Portfolio: average DR per timestamp across targets that have a sample that day.
  const byDay = new Map<string, number[]>();
  for (const t of targets) {
    for (const s of hist[t.target] ?? []) {
      const day = s.at.slice(0, 10);
      const list = byDay.get(day) ?? [];
      list.push(s.domainRating);
      byDay.set(day, list);
    }
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([at, vals]) => ({
      at,
      value: Math.round((vals.reduce((x, y) => x + y, 0) / vals.length) * 10) / 10,
    }));
}

export function ahrefsGrowthForProductScope(
  ahrefs: ReturnType<typeof parseAhrefsMeta>,
  product: { id: string; name: string } | null,
): AhrefsGrowthView | null {
  if (!ahrefs) return null;
  if (!product) {
    const deltas30 = ahrefs.targets
      .map((t) => t.drDelta30d)
      .filter((n): n is number => n !== null);
    const deltas90 = ahrefs.targets
      .map((t) => t.drDelta90d)
      .filter((n): n is number => n !== null);
    const sinceBase = ahrefs.targets
      .map((t) => t.drDeltaSinceBaseline)
      .filter((n): n is number => n !== null);
    const keywords = ahrefs.targets
      .map((t) => t.orgKeywords)
      .filter((n): n is number => n !== null);
    const top3 = ahrefs.targets
      .map((t) => t.orgKeywordsTop3)
      .filter((n): n is number => n !== null);
    return {
      scopeLabel: "All products",
      domainRating: ahrefs.domainRatingAvg,
      drDelta30d: deltas30.length
        ? Math.round(
            (deltas30.reduce((a, b) => a + b, 0) / deltas30.length) * 10,
          ) / 10
        : null,
      drDelta90d: deltas90.length
        ? Math.round(
            (deltas90.reduce((a, b) => a + b, 0) / deltas90.length) * 10,
          ) / 10
        : null,
      drDeltaSinceBaseline: sinceBase.length
        ? Math.round(
            (sinceBase.reduce((a, b) => a + b, 0) / sinceBase.length) * 10,
          ) / 10
        : null,
      baselineDr: null,
      liveRefdomains: ahrefs.liveRefdomainsTotal,
      orgTraffic: ahrefs.orgTrafficTotal,
      orgKeywords: keywords.length
        ? keywords.reduce((a, b) => a + b, 0)
        : null,
      orgKeywordsTop3: top3.length ? top3.reduce((a, b) => a + b, 0) : null,
      targetCount: ahrefs.targets.length,
      unmatched: false,
      targets: ahrefs.targets,
      apiMode: ahrefs.apiMode,
      attribution: ahrefs.attribution,
      series: ahrefsSeriesForTargets(ahrefs, ahrefs.targets),
    };
  }
  const scoped = ahrefs.targets.filter((t) => t.productId === product.id);
  if (!scoped.length) {
    return {
      scopeLabel: product.name,
      domainRating: null,
      drDelta30d: null,
      drDelta90d: null,
      drDeltaSinceBaseline: null,
      baselineDr: null,
      liveRefdomains: null,
      orgTraffic: null,
      orgKeywords: null,
      orgKeywordsTop3: null,
      targetCount: 0,
      unmatched: true,
      targets: [],
      apiMode: ahrefs.apiMode,
      attribution: ahrefs.attribution,
      series: [],
    };
  }
  const primary = scoped[0]!;
  return {
    scopeLabel: product.name,
    domainRating: primary.domainRating,
    drDelta30d: primary.drDelta30d,
    drDelta90d: primary.drDelta90d,
    drDeltaSinceBaseline: primary.drDeltaSinceBaseline ?? null,
    baselineDr: primary.baselineDr ?? null,
    liveRefdomains: primary.liveRefdomains,
    orgTraffic: primary.orgTraffic,
    orgKeywords: primary.orgKeywords,
    orgKeywordsTop3: primary.orgKeywordsTop3,
    targetCount: scoped.length,
    unmatched: false,
    targets: scoped,
    apiMode: ahrefs.apiMode,
    attribution: ahrefs.attribution,
    series: ahrefsSeriesForTargets(ahrefs, scoped),
  };
}

export type PostHogSessionView = {
  id: string;
  startUrl: string | null;
  startTime: string | null;
  durationSec: number | null;
  personDisplay: string | null;
  replayUrl: string;
};

export type PostHogProjectView = {
  productId: string;
  host: string;
  projectId: string;
  label?: string;
  signupEvent: string;
  firstValueEvent: string;
  mapped: boolean;
  signupCount1d: number | null;
  signupCount7d: number | null;
  signupCount30d: number | null;
  signupCount7dPrior: number | null;
  signupCount30dPrior: number | null;
  firstValueCount1d: number | null;
  firstValueCount7d: number | null;
  firstValueCount30d: number | null;
  firstValueCount7dPrior: number | null;
  firstValueCount30dPrior: number | null;
  exceptionCount7d: number | null;
  openErrorIssues: number | null;
  sessions: PostHogSessionView[];
};

export function parsePostHogMeta(metaJson: string | null): {
  projects: PostHogProjectView[];
  signupCount7dTotal: number | null;
  firstValueCount7dTotal: number | null;
  signupCount7dPriorTotal: number | null;
  firstValueCount7dPriorTotal: number | null;
  signupCount30dTotal: number | null;
  firstValueCount30dTotal: number | null;
  signupCount30dPriorTotal: number | null;
  firstValueCount30dPriorTotal: number | null;
  exceptionCount7dTotal: number | null;
  openErrorIssuesTotal: number | null;
  unmappedCount: number;
  activationHistory: Record<
    string,
    Array<{ at: string; signups: number; firstValue: number }>
  >;
} | null {
  if (!metaJson) return null;
  try {
    const meta = JSON.parse(metaJson) as {
      projects?: PostHogProjectView[];
      signupCount7dTotal?: number | null;
      firstValueCount7dTotal?: number | null;
      signupCount7dPriorTotal?: number | null;
      firstValueCount7dPriorTotal?: number | null;
      signupCount30dTotal?: number | null;
      firstValueCount30dTotal?: number | null;
      signupCount30dPriorTotal?: number | null;
      firstValueCount30dPriorTotal?: number | null;
      exceptionCount7dTotal?: number | null;
      openErrorIssuesTotal?: number | null;
      unmappedCount?: number;
      activationHistory?: Record<
        string,
        Array<{ at: string; signups: number; firstValue: number }>
      >;
    };
    const projects = Array.isArray(meta.projects)
      ? meta.projects.filter((p) => p?.productId && p?.projectId)
      : [];
    if (!projects.length) return null;
    const sum = (pick: (p: PostHogProjectView) => number | null | undefined) => {
      let t = 0;
      let any = false;
      for (const p of projects) {
        const n = pick(p);
        if (n != null) {
          t += n;
          any = true;
        }
      }
      return any ? t : null;
    };
    return {
      projects,
      signupCount7dTotal: meta.signupCount7dTotal ?? sum((p) => p.signupCount7d),
      firstValueCount7dTotal:
        meta.firstValueCount7dTotal ?? sum((p) => p.firstValueCount7d),
      signupCount7dPriorTotal:
        meta.signupCount7dPriorTotal ?? sum((p) => p.signupCount7dPrior),
      firstValueCount7dPriorTotal:
        meta.firstValueCount7dPriorTotal ??
        sum((p) => p.firstValueCount7dPrior),
      signupCount30dTotal:
        meta.signupCount30dTotal ?? sum((p) => p.signupCount30d),
      firstValueCount30dTotal:
        meta.firstValueCount30dTotal ?? sum((p) => p.firstValueCount30d),
      signupCount30dPriorTotal:
        meta.signupCount30dPriorTotal ?? sum((p) => p.signupCount30dPrior),
      firstValueCount30dPriorTotal:
        meta.firstValueCount30dPriorTotal ??
        sum((p) => p.firstValueCount30dPrior),
      exceptionCount7dTotal: meta.exceptionCount7dTotal ?? null,
      openErrorIssuesTotal: meta.openErrorIssuesTotal ?? null,
      unmappedCount:
        meta.unmappedCount ?? projects.filter((p) => !p.mapped).length,
      activationHistory:
        meta.activationHistory && typeof meta.activationHistory === "object"
          ? meta.activationHistory
          : {},
    };
  } catch {
    return null;
  }
}

function posthogSignupSeries(
  ph: NonNullable<ReturnType<typeof parsePostHogMeta>>,
  productIds: string[],
): Array<{ at: string; value: number }> {
  const byDay = new Map<string, number>();
  for (const id of productIds) {
    for (const s of ph.activationHistory[id] ?? []) {
      const day = s.at.slice(0, 10);
      byDay.set(day, (byDay.get(day) ?? 0) + (s.signups ?? 0));
    }
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([at, value]) => ({ at, value }));
}

export function posthogGrowthForProductScope(
  ph: ReturnType<typeof parsePostHogMeta>,
  product: { id: string; name: string } | null,
) {
  if (!ph) return null;
  if (!product) {
    return {
      scopeLabel: "All products",
      unmatched: false,
      signupCount1d: sumProjects(ph.projects, (p) => p.signupCount1d),
      signupCount7d: ph.signupCount7dTotal,
      signupCount30d: ph.signupCount30dTotal,
      signupCount7dPrior: ph.signupCount7dPriorTotal,
      signupCount30dPrior: ph.signupCount30dPriorTotal,
      firstValueCount1d: sumProjects(ph.projects, (p) => p.firstValueCount1d),
      firstValueCount7d: ph.firstValueCount7dTotal,
      firstValueCount30d: ph.firstValueCount30dTotal,
      firstValueCount7dPrior: ph.firstValueCount7dPriorTotal,
      firstValueCount30dPrior: ph.firstValueCount30dPriorTotal,
      exceptionCount7d: ph.exceptionCount7dTotal,
      openErrorIssues: ph.openErrorIssuesTotal,
      unmappedCount: ph.unmappedCount,
      projectCount: ph.projects.length,
      sessions: ph.projects.flatMap((p) =>
        p.sessions.map((s) => ({
          ...s,
          productId: p.productId,
          productLabel: p.label,
        })),
      ),
      projects: ph.projects,
      signupSeries: posthogSignupSeries(
        ph,
        ph.projects.map((p) => p.productId),
      ),
    };
  }
  const scoped = ph.projects.filter((p) => p.productId === product.id);
  if (!scoped.length) {
    return {
      scopeLabel: product.name,
      unmatched: true,
      signupCount1d: null as number | null,
      signupCount7d: null as number | null,
      signupCount30d: null as number | null,
      signupCount7dPrior: null as number | null,
      signupCount30dPrior: null as number | null,
      firstValueCount1d: null as number | null,
      firstValueCount7d: null as number | null,
      firstValueCount30d: null as number | null,
      firstValueCount7dPrior: null as number | null,
      firstValueCount30dPrior: null as number | null,
      exceptionCount7d: null as number | null,
      openErrorIssues: null as number | null,
      unmappedCount: 0,
      projectCount: 0,
      sessions: [] as Array<
        PostHogSessionView & { productId: string; productLabel?: string }
      >,
      projects: [] as PostHogProjectView[],
      signupSeries: [] as Array<{ at: string; value: number }>,
    };
  }
  const sum = (pick: (p: PostHogProjectView) => number | null) => {
    let t = 0;
    let any = false;
    for (const p of scoped) {
      const n = pick(p);
      if (n != null) {
        t += n;
        any = true;
      }
    }
    return any ? t : null;
  };
  return {
    scopeLabel: product.name,
    unmatched: false,
    signupCount1d: sum((p) => p.signupCount1d),
    signupCount7d: sum((p) => p.signupCount7d),
    signupCount30d: sum((p) => p.signupCount30d),
    signupCount7dPrior: sum((p) => p.signupCount7dPrior),
    signupCount30dPrior: sum((p) => p.signupCount30dPrior),
    firstValueCount1d: sum((p) => p.firstValueCount1d),
    firstValueCount7d: sum((p) => p.firstValueCount7d),
    firstValueCount30d: sum((p) => p.firstValueCount30d),
    firstValueCount7dPrior: sum((p) => p.firstValueCount7dPrior),
    firstValueCount30dPrior: sum((p) => p.firstValueCount30dPrior),
    exceptionCount7d: sum((p) => p.exceptionCount7d),
    openErrorIssues: sum((p) => p.openErrorIssues),
    unmappedCount: scoped.filter((p) => !p.mapped).length,
    projectCount: scoped.length,
    sessions: scoped.flatMap((p) =>
      p.sessions.map((s) => ({
        ...s,
        productId: p.productId,
        productLabel: p.label,
      })),
    ),
    projects: scoped,
    signupSeries: posthogSignupSeries(
      ph,
      scoped.map((p) => p.productId),
    ),
  };
}

function sumProjects(
  projects: PostHogProjectView[],
  pick: (p: PostHogProjectView) => number | null | undefined,
): number | null {
  let t = 0;
  let any = false;
  for (const p of projects) {
    const n = pick(p);
    if (n != null) {
      t += n;
      any = true;
    }
  }
  return any ? t : null;
}

function normalizeMatchKey(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/** Match Buffer channel names to a product (slug/name heuristics). */
export function matchBufferChannelsToProduct(
  stats: BufferStatsView,
  product: { name: string; slug: string },
): BufferChannelStatsView[] {
  const keys = [normalizeMatchKey(product.slug), normalizeMatchKey(product.name)].filter(
    (k) => k.length >= 3,
  );
  if (!keys.length) return [];

  const slices =
    stats.channelStats && stats.channelStats.length > 0
      ? stats.channelStats
      : (stats.channels ?? []).map((ch) => ({
          channelId: ch.id ?? ch.name,
          name: ch.name,
          service: ch.service,
          scheduledCount: null,
          published7d: null,
          clicks7d: null,
          impressions7d: null,
          comments7d: null,
          reactions7d: null,
          reposts7d: null,
          shares7d: null,
        }));

  return slices.filter((ch) => {
    const cn = normalizeMatchKey(ch.name);
    if (!cn) return false;
    return keys.some((k) => cn.includes(k) || k.includes(cn));
  });
}

function sumNullable(
  values: (number | null | undefined)[],
): number | null {
  let sum = 0;
  let seen = false;
  for (const v of values) {
    if (v === null || v === undefined) continue;
    sum += v;
    seen = true;
  }
  return seen ? sum : null;
}

export type BufferQueueBand = "red" | "yellow" | "green";

/**
 * Queue refill bands — per channel: &lt;3 red, 3–6 yellow, &gt;6 green.
 * Consolidated scopes scale by channel count (e.g. 3 channels → &lt;9 / 9–18 / &gt;18).
 */
export function bufferQueueBand(
  scheduledCount: number | null | undefined,
  channelCount: number,
): BufferQueueBand | null {
  if (
    scheduledCount == null ||
    !Number.isFinite(scheduledCount) ||
    channelCount < 1
  ) {
    return null;
  }
  const redBelow = 3 * channelCount;
  const greenAbove = 6 * channelCount;
  if (scheduledCount < redBelow) return "red";
  if (scheduledCount > greenAbove) return "green";
  return "yellow";
}

export function bufferQueueBandLabel(
  band: BufferQueueBand | null,
  channelCount: number,
): string | null {
  if (!band || channelCount < 1) return null;
  const redBelow = 3 * channelCount;
  const greenAbove = 6 * channelCount;
  if (band === "red") {
    return channelCount === 1
      ? "below 3 — refill"
      : `below ${redBelow} across ${channelCount} channels — refill`;
  }
  if (band === "yellow") {
    return channelCount === 1
      ? "3–6 — topping up soon"
      : `${redBelow}–${greenAbove} across ${channelCount} channels — topping up soon`;
  }
  return channelCount === 1
    ? "above 6 — stocked"
    : `above ${greenAbove} across ${channelCount} channels — stocked`;
}

export type BufferMetricWindow = "day" | "week" | "month";

function bufferEngRate(
  impressions: number | null,
  parts: Array<number | null>,
): number | null {
  if (impressions == null || impressions <= 0) return null;
  let sum = 0;
  let seen = false;
  for (const p of parts) {
    if (p == null) continue;
    sum += p;
    seen = true;
  }
  if (!seen) return null;
  return Math.round((sum / impressions) * 1000) / 10;
}

export type BufferGrowthView = {
  scopeLabel: string;
  channelNames: string[];
  /** Channels included in this scope (drives scaled queue bands). */
  channelCount: number;
  /** Pending scheduled posts in scope (org or matched product channels). */
  scheduledCount: number | null;
  scheduledTruncated: boolean;
  published: number | null;
  publishedTruncated: boolean;
  publishedPrior: number | null;
  impressions: number | null;
  impressionsPrior: number | null;
  clicks: number | null;
  clicksPrior: number | null;
  comments: number | null;
  commentsPrior: number | null;
  reactions: number | null;
  reactionsPrior: number | null;
  reposts: number | null;
  repostsPrior: number | null;
  /** (reactions+comments+reposts+clicks) / impressions — null when impressions missing. */
  engRatePct: number | null;
  engRatePctPrior: number | null;
  windowLabel: string;
  compareLabel: string;
  unmatched: boolean;
};

/** Org totals for All products; product-matched channel sum otherwise. */
export function bufferStatsForProductScope(
  stats: BufferStatsView,
  product: { name: string; slug: string } | null,
  window: BufferMetricWindow = "week",
): BufferGrowthView {
  const useMonth = window === "month";
  const useDay = window === "day";
  const windowLabel = useMonth ? "30d" : useDay ? "day" : "week";
  const compareLabel = useMonth
    ? "vs prior 30d"
    : useDay
      ? "vs prior day"
      : "vs prior week";

  if (!product) {
    const channelCount = Math.max(
      1,
      stats.channels?.length ??
        stats.channelCount ??
        stats.channelStats?.length ??
        1,
    );
    const published = useMonth
      ? (stats.published30d ?? stats.postCount30d ?? null)
      : useDay
        ? (stats.published1d ?? stats.postCount1d ?? null)
        : (stats.published7d ?? stats.postCount7d ?? null);
    const publishedPrior = useMonth
      ? (stats.published30dPrior ?? stats.postCount30dPrior ?? null)
      : useDay
        ? (stats.published1dPrior ?? stats.postCount1dPrior ?? null)
        : (stats.postCount7dPrior ?? null);
    const impressions = useMonth
      ? (stats.impressions30d ?? null)
      : useDay
        ? (stats.impressions1d ?? null)
        : (stats.impressions7d ?? null);
    const impressionsPrior = useMonth
      ? (stats.impressions30dPrior ?? null)
      : useDay
        ? (stats.impressions1dPrior ?? null)
        : (stats.impressions7dPrior ?? null);
    const clicks = useMonth
      ? (stats.clicks30d ?? null)
      : useDay
        ? (stats.clicks1d ?? null)
        : (stats.clicks7d ?? null);
    const clicksPrior = useMonth
      ? (stats.clicks30dPrior ?? null)
      : useDay
        ? (stats.clicks1dPrior ?? null)
        : (stats.clicks7dPrior ?? null);
    const comments = useMonth
      ? (stats.comments30d ?? null)
      : useDay
        ? (stats.comments1d ?? null)
        : (stats.comments7d ?? null);
    const commentsPrior = useMonth
      ? (stats.comments30dPrior ?? null)
      : useDay
        ? (stats.comments1dPrior ?? null)
        : (stats.comments7dPrior ?? null);
    const reactions = useMonth
      ? (stats.reactions30d ?? null)
      : useDay
        ? (stats.reactions1d ?? null)
        : (stats.reactions7d ?? null);
    const reactionsPrior = useMonth
      ? (stats.reactions30dPrior ?? null)
      : useDay
        ? (stats.reactions1dPrior ?? null)
        : (stats.reactions7dPrior ?? null);
    const reposts = useMonth
      ? (stats.reposts30d ?? stats.shares30d ?? null)
      : useDay
        ? (stats.reposts1d ?? stats.shares1d ?? null)
        : (stats.reposts7d ?? stats.shares7d ?? null);
    const repostsPrior = useMonth
      ? (stats.reposts30dPrior ?? stats.shares30dPrior ?? null)
      : useDay
        ? (stats.reposts1dPrior ?? stats.shares1dPrior ?? null)
        : (stats.reposts7dPrior ?? stats.shares7dPrior ?? null);

    return {
      scopeLabel: "All channels",
      channelNames: (stats.channels ?? []).map((c) => c.name),
      channelCount,
      scheduledCount:
        typeof stats.scheduledCount === "number" ? stats.scheduledCount : null,
      scheduledTruncated: Boolean(stats.scheduledTruncated),
      published,
      publishedTruncated:
        useMonth || useDay ? false : Boolean(stats.published7dTruncated),
      publishedPrior,
      impressions,
      impressionsPrior,
      clicks,
      clicksPrior,
      comments,
      commentsPrior,
      reactions,
      reactionsPrior,
      reposts,
      repostsPrior,
      engRatePct: bufferEngRate(impressions, [
        reactions,
        comments,
        reposts,
        clicks,
      ]),
      engRatePctPrior: bufferEngRate(impressionsPrior, [
        reactionsPrior,
        commentsPrior,
        repostsPrior,
        clicksPrior,
      ]),
      windowLabel,
      compareLabel,
      unmatched: false,
    };
  }

  const matched = matchBufferChannelsToProduct(stats, product);
  if (matched.length === 0) {
    return {
      scopeLabel: product.name,
      channelNames: [],
      channelCount: 0,
      scheduledCount: null,
      scheduledTruncated: false,
      published: null,
      publishedTruncated: false,
      publishedPrior: null,
      impressions: null,
      impressionsPrior: null,
      clicks: null,
      clicksPrior: null,
      comments: null,
      commentsPrior: null,
      reactions: null,
      reactionsPrior: null,
      reposts: null,
      repostsPrior: null,
      engRatePct: null,
      engRatePctPrior: null,
      windowLabel,
      compareLabel,
      unmatched: true,
    };
  }

  const published = sumNullable(matched.map((c) => c.published7d));
  const impressions = sumNullable(matched.map((c) => c.impressions7d));
  const clicks = sumNullable(matched.map((c) => c.clicks7d));
  const comments = sumNullable(matched.map((c) => c.comments7d));
  const reactions = sumNullable(matched.map((c) => c.reactions7d));
  const reposts = sumNullable(
    matched.map((c) => c.reposts7d ?? c.shares7d ?? null),
  );

  const orgOnlyWindow = useMonth || useDay;
  return {
    scopeLabel: matched.map((c) => c.name).join(", "),
    channelNames: matched.map((c) => c.name),
    channelCount: matched.length,
    scheduledCount: sumNullable(matched.map((c) => c.scheduledCount)),
    scheduledTruncated: Boolean(stats.scheduledTruncated),
    published: orgOnlyWindow ? null : published,
    publishedTruncated: false,
    publishedPrior: null,
    impressions: orgOnlyWindow ? null : impressions,
    impressionsPrior: null,
    clicks: orgOnlyWindow ? null : clicks,
    clicksPrior: null,
    comments: orgOnlyWindow ? null : comments,
    commentsPrior: null,
    reactions: orgOnlyWindow ? null : reactions,
    reactionsPrior: null,
    reposts: orgOnlyWindow ? null : reposts,
    repostsPrior: null,
    engRatePct: orgOnlyWindow
      ? null
      : bufferEngRate(impressions, [reactions, comments, reposts, clicks]),
    engRatePctPrior: null,
    windowLabel: orgOnlyWindow
      ? `${windowLabel} (org — pick All products)`
      : windowLabel,
    compareLabel,
    unmatched: false,
  };
}

function formatDue(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  const diff = t - Date.now();
  if (diff < 0) return "overdue / due soon";
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `in ${hrs}h`;
  return new Date(t).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function BufferStatsStrip({ stats }: { stats: BufferStatsView }) {
  const plus = (n: number | undefined, truncated?: boolean) =>
    `${n ?? 0}${truncated ? "+" : ""}`;
  const fmtMetric = (n: number | null | undefined) =>
    n === null || n === undefined ? "—" : String(n);
  const channelCount = Math.max(
    1,
    stats.channels?.length ?? stats.channelCount ?? 1,
  );
  const queueBand = bufferQueueBand(stats.scheduledCount ?? null, channelCount);

  return (
    <div className="op-buffer-stats">
      <div className="op-buffer-stat-row" aria-label="Buffer wave 1">
        <div
          className={`op-buffer-stat${queueBand ? ` is-queue-${queueBand}` : ""}`}
        >
          <strong>{plus(stats.scheduledCount, stats.scheduledTruncated)}</strong>
          <span>Queued</span>
        </div>
        <div className="op-buffer-stat">
          <strong>{plus(stats.published7d, stats.published7dTruncated)}</strong>
          <span>Sent 7d</span>
        </div>
        <div className="op-buffer-stat">
          <strong>{stats.errorCount ?? 0}</strong>
          <span>Errors</span>
        </div>
        <div className="op-buffer-stat">
          <strong>{stats.pausedChannelCount ?? 0}</strong>
          <span>Paused</span>
        </div>
        <div className="op-buffer-stat">
          <strong>{stats.channelCount ?? 0}</strong>
          <span>Channels</span>
        </div>
      </div>
      <div className="op-buffer-stat-row op-buffer-stat-row-2" aria-label="Buffer wave 2">
        <div className="op-buffer-stat">
          <strong>{formatDue(stats.nextDueAt)}</strong>
          <span>Next due</span>
        </div>
        <div className="op-buffer-stat">
          <strong>
            {stats.dailySent ?? 0}
            {stats.dailyLimit != null ? `/${stats.dailyLimit}` : ""}
            {stats.dailyAtLimit ? "!" : ""}
          </strong>
          <span>Today sent{stats.dailyLimit != null ? "/limit" : ""}</span>
        </div>
        <div className="op-buffer-stat">
          <strong>{fmtMetric(stats.impressions7d)}</strong>
          <span>Impressions 7d</span>
        </div>
        <div className="op-buffer-stat">
          <strong>{fmtMetric(stats.clicks7d)}</strong>
          <span>Clicks 7d</span>
        </div>
        <div className="op-buffer-stat">
          <strong>{fmtMetric(stats.comments7d)}</strong>
          <span>Comments 7d</span>
        </div>
        <div className="op-buffer-stat">
          <strong>{fmtMetric(stats.reposts7d ?? stats.shares7d)}</strong>
          <span>Reposts 7d</span>
        </div>
      </div>
      {stats.channels && stats.channels.length > 0 ? (
        <ul className="op-buffer-channels">
          {stats.channels.slice(0, 8).map((ch) => {
            const slice = stats.channelStats?.find(
              (c) =>
                c.channelId === ch.id ||
                (c.name === ch.name && c.service === ch.service),
            );
            const queued =
              typeof slice?.scheduledCount === "number"
                ? slice.scheduledCount
                : null;
            const band = bufferQueueBand(queued, 1);
            return (
              <li
                key={`${ch.name}-${ch.service}`}
                className={band ? `is-queue-${band}` : undefined}
              >
                {ch.name}
                {ch.service ? ` · ${ch.service}` : ""}
                {queued != null ? ` · ${queued} queued` : ""}
                {ch.isQueuePaused ? " · paused" : ""}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function fmtRatePct(rate: number | null | undefined): string {
  if (rate == null || !Number.isFinite(rate)) return "—";
  return `${Math.round(rate * 1000) / 10}%`;
}

function InstantlyStatsStrip({ stats }: { stats: InstantlyStatsView }) {
  const sent7d = stats.sent7d ?? 0;
  const accountCount =
    stats.accountCount ?? stats.warmup?.accounts?.length ?? 0;
  const warmupMode = sent7d === 0 && accountCount > 0;
  const warmup = stats.warmup;

  if (warmupMode) {
    return (
      <div className="op-buffer-stats">
        <div className="op-buffer-stat-row" aria-label="Instantly warmup health">
          <div className="op-buffer-stat">
            <strong>
              {warmup?.avgHealthScore != null
                ? warmup.avgHealthScore
                : "—"}
            </strong>
            <span>Avg health</span>
          </div>
          <div className="op-buffer-stat">
            <strong>{accountCount}</strong>
            <span>Accounts</span>
          </div>
          <div className="op-buffer-stat">
            <strong>{fmtRatePct(warmup?.inboxRate)}</strong>
            <span>Inbox land</span>
          </div>
          <div className="op-buffer-stat">
            <strong>{fmtRatePct(warmup?.spamRate)}</strong>
            <span>Spam land</span>
          </div>
        </div>
        {stats.warmupError ? (
          <p className="op-growth-buffer-note">{stats.warmupError}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="op-buffer-stats">
      <div className="op-buffer-stat-row" aria-label="Instantly outbound 7d">
        <div className="op-buffer-stat">
          <strong>{stats.sent7d ?? "—"}</strong>
          <span>Sent 7d</span>
        </div>
        <div className="op-buffer-stat">
          <strong>{stats.opened7d ?? "—"}</strong>
          <span>Opened</span>
        </div>
        <div className="op-buffer-stat">
          <strong>{stats.clicked7d ?? "—"}</strong>
          <span>Clicked</span>
        </div>
        <div className="op-buffer-stat">
          <strong>{stats.replied7d ?? "—"}</strong>
          <span>Replied</span>
        </div>
        <div className="op-buffer-stat">
          <strong>{accountCount || (stats.campaignCount ?? "—")}</strong>
          <span>{accountCount ? "Accounts" : "Campaigns"}</span>
        </div>
      </div>
    </div>
  );
}

type Props = {
  products: Product[];
  integrations: IntegrationCard[];
  loading: boolean;
  error: string;
  notice: string;
  actionError: string;
  secretsKeyConfigured: boolean;
  busy: string;
  pickerOpen: boolean;
  draftProvider: string | null;
  tuneProvider: string | null;
  bufferApiKey: string;
  instantlyApiKey: string;
  googleClientId: string;
  googleClientSecret: string;
  ahrefsApiKey: string;
  ahrefsPlatformKeyConfigured: boolean;
  onPickerOpen: (open: boolean) => void;
  onDraftProvider: (provider: string | null) => void;
  onTuneProvider: (provider: string | null) => void;
  onBufferApiKey: (v: string) => void;
  onInstantlyApiKey: (v: string) => void;
  onGoogleClientId: (v: string) => void;
  onGoogleClientSecret: (v: string) => void;
  onAhrefsApiKey: (v: string) => void;
  onGoogleAuthorize: (payload: {
    properties: PropertyRow[];
    sites: SiteRow[];
  }) => void;
  onGoogleSaveMappings: (
    payload: {
      properties: PropertyRow[];
      sites: SiteRow[];
    },
    surface: "ga" | "gsc",
  ) => void;
  onAhrefsSave: (payload: {
    targets: { target: string; productId: string }[];
  }) => void;
  onPostHogSave: (payload: {
    projects: {
      productId: string;
      host: string;
      projectId: string;
      apiKey?: string;
      signupEvent: string;
      firstValueEvent: string;
    }[];
  }) => void;
  onAction: (
    provider: string,
    action: "connect" | "sync" | "disconnect",
    body?: Record<string, unknown>,
  ) => void;
};

/** Strip quotes/whitespace pasted from Cloud Console. */
export function cleanSecret(raw: string): string {
  return raw.trim().replace(/^["']+|["']+$/g, "");
}

function Field(props: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="op-conn-field">
      <span className="op-conn-field-label">{props.label}</span>
      {props.children}
      {props.hint ? <small className="op-conn-field-hint">{props.hint}</small> : null}
    </label>
  );
}

export function ConnectionsPanel(props: Props) {
  const modalTitleId = useId();
  const modalRef = useRef<HTMLDivElement>(null);
  const addBtnRef = useRef<HTMLButtonElement>(null);
  const [ahrefsTargets, setAhrefsTargets] = useState<
    { target: string; productId: string }[]
  >(() => [{ target: "", productId: props.products[0]?.id ?? "" }]);
  const [ahrefsHydrated, setAhrefsHydrated] = useState(false);
  const [posthogRows, setPosthogRows] = useState<
    {
      productId: string;
      host: string;
      projectId: string;
      apiKey: string;
      signupEvent: string;
      firstValueEvent: string;
    }[]
  >(() => [
    {
      productId: props.products[0]?.id ?? "",
      host: "https://eu.posthog.com",
      projectId: "",
      apiKey: "",
      signupEvent: "",
      firstValueEvent: "",
    },
  ]);
  const [posthogHydrated, setPosthogHydrated] = useState(false);

  const active = props.integrations.filter(isActiveModule);
  const liveTotal = CONNECTION_CATALOG.filter((c) => c.live).length;
  const lit = active.filter(
    (i) => i.status === "connected" || i.status === "stale",
  ).length;
  const progressPct = liveTotal ? Math.round((lit / liveTotal) * 100) : 0;

  const activeProviders = new Set(active.map((i) => i.provider));

  const catalog = (provider: string) =>
    CONNECTION_CATALOG.find((c) => c.provider === provider);

  function closeModal() {
    props.onPickerOpen(false);
    props.onDraftProvider(null);
  }

  function openModal() {
    props.onDraftProvider(null);
    props.onTuneProvider(null);
    props.onPickerOpen(true);
  }

  useEffect(() => {
    if (!props.pickerOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const prev = document.activeElement;
    const t = window.setTimeout(() => {
      modalRef.current
        ?.querySelector<HTMLElement>("button, [href], input, textarea")
        ?.focus();
    }, 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (props.draftProvider) props.onDraftProvider(null);
        else closeModal();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      if (prev instanceof HTMLElement) prev.focus();
      else addBtnRef.current?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.pickerOpen, props.draftProvider]);

  useEffect(() => {
    if (ahrefsHydrated) return;
    const row = props.integrations.find((i) => i.provider === "ahrefs");
    const meta = parseAhrefsMeta(row?.metaJson ?? null);
    const fallback = props.products[0]?.id ?? "";
    if (meta?.targets.length) {
      setAhrefsTargets(
        meta.targets.map((t) => ({
          target: t.target,
          productId: t.productId || fallback,
        })),
      );
      setAhrefsHydrated(true);
      return;
    }
    if (props.products.length > 0) {
      setAhrefsTargets(
        props.products.map((p) => {
          let host = "";
          if (p.url) {
            try {
              host = new URL(
                p.url.startsWith("http") ? p.url : `https://${p.url}`,
              ).hostname.replace(/^www\./, "");
            } catch {
              host = "";
            }
          }
          return { target: host, productId: p.id };
        }),
      );
      setAhrefsHydrated(true);
    }
  }, [props.integrations, props.products, ahrefsHydrated]);

  useEffect(() => {
    if (posthogHydrated) return;
    const row = props.integrations.find((i) => i.provider === "posthog");
    const meta = parsePostHogMeta(row?.metaJson ?? null);
    const fallback = props.products[0]?.id ?? "";
    if (meta?.projects.length) {
      setPosthogRows(
        meta.projects.map((p) => ({
          productId: p.productId || fallback,
          host: p.host || "https://eu.posthog.com",
          projectId: p.projectId,
          apiKey: "",
          signupEvent: p.signupEvent || "",
          firstValueEvent: p.firstValueEvent || "",
        })),
      );
      setPosthogHydrated(true);
      return;
    }
    if (props.products.length > 0) {
      setPosthogRows(
        props.products.map((p) => ({
          productId: p.id,
          host: "https://eu.posthog.com",
          projectId: "",
          apiKey: "",
          signupEvent: "",
          firstValueEvent: "",
        })),
      );
      setPosthogHydrated(true);
    }
  }, [props.integrations, props.products, posthogHydrated]);

  function googleSecretsStored(): boolean {
    return props.integrations.some(
      (i) =>
        (i.provider === "ga" || i.provider === "gsc") &&
        i.credentialsConfigured,
    );
  }

  function initialGoogleRows(): {
    properties: PropertyRow[];
    sites: SiteRow[];
  } {
    const ga = props.integrations.find((i) => i.provider === "ga");
    const gsc = props.integrations.find((i) => i.provider === "gsc");
    const gaMeta = parseGaMeta(ga?.metaJson ?? null);
    const gscMeta = parseGscMeta(gsc?.metaJson ?? null);
    const fallbackProduct = props.products[0]?.id ?? "";
    const properties: PropertyRow[] =
      gaMeta?.properties.map((p) => ({
        propertyId: p.propertyId,
        productId: p.productId || fallbackProduct,
      })) ?? [];
    const sites: SiteRow[] =
      gscMeta?.sites.map((s) => ({
        siteUrl: s.siteUrl,
        productId: s.productId || fallbackProduct,
      })) ?? [];
    return { properties, sites };
  }

  function missingForSave(provider: string): string[] {
    const missing: string[] = [];
    if (provider === "buffer") {
      const stored = props.integrations.find(
        (i) => i.provider === "buffer",
      )?.credentialsConfigured;
      if (!props.bufferApiKey.trim() && !stored) {
        missing.push("Buffer API key");
      }
    }
    if (provider === "instantly") {
      const stored = props.integrations.find(
        (i) => i.provider === "instantly",
      )?.credentialsConfigured;
      if (!props.instantlyApiKey.trim() && !stored) {
        missing.push("Instantly API key");
      }
    }
    if (provider === "ahrefs") {
      const stored = props.integrations.find(
        (i) => i.provider === "ahrefs",
      )?.credentialsConfigured;
      if (
        !props.ahrefsApiKey.trim() &&
        !stored &&
        !props.ahrefsPlatformKeyConfigured
      ) {
        missing.push("Ahrefs API key (or set AHREFS_API_KEY on the Worker)");
      }
      const valid = ahrefsTargets.filter((t) => t.target.trim() && t.productId);
      if (valid.length === 0 && !props.ahrefsPlatformKeyConfigured) {
        missing.push(
          "at least one domain → product (or set product website URLs)",
        );
      }
    }
    if (provider === "posthog") {
      const stored = props.integrations.find(
        (i) => i.provider === "posthog",
      )?.credentialsConfigured;
      const valid = posthogRows.filter(
        (r) => r.productId && r.projectId.trim() && (r.apiKey.trim() || stored),
      );
      if (valid.length === 0) {
        missing.push("at least one product with project id + API key");
      }
    }
    return missing;
  }

  function renderAhrefsForm() {
    const stored = props.integrations.find(
      (i) => i.provider === "ahrefs",
    )?.credentialsConfigured;
    const platformReady = props.ahrefsPlatformKeyConfigured;
    return (
      <div className="op-google-wizard">
        <p className="op-conn-hint">
          One Outpost Ahrefs key covers every product domain (public Domain
          Rating API — unlimited targets per account). Prefer setting{" "}
          <code>AHREFS_API_KEY</code> once on the Worker; domains are prefilled
          from product websites. Paste a full Site Explorer key only if you want
          richer metrics later.
        </p>
        {platformReady ? (
          <p className="op-conn-hint op-conn-hint-ok">
            Platform Ahrefs key is configured — no paste needed. Map domains and
            pull DR.
          </p>
        ) : (
          <Field
            label="Ahrefs API key (optional if platform key set)"
            hint="Public key = DR only. Or run: wrangler secret put AHREFS_API_KEY"
          >
            <input
              className="op-field"
              type="password"
              value={props.ahrefsApiKey}
              onChange={(e) => props.onAhrefsApiKey(e.target.value)}
              placeholder={
                stored ? "•••• stored — paste to rotate" : "Paste API v3 key"
              }
              autoComplete="off"
              spellCheck={false}
            />
          </Field>
        )}
        <div className="op-google-map-block">
          <div className="op-google-map-head">
            <strong>Product domains</strong>
            <button
              type="button"
              className="op-btn ghost"
              onClick={() =>
                setAhrefsTargets((rows) => [
                  ...rows,
                  {
                    target: "",
                    productId: props.products[0]?.id ?? "",
                  },
                ])
              }
            >
              Add domain
            </button>
          </div>
          {ahrefsTargets.map((row, idx) => (
            <div key={`ahrefs-${idx}`} className="op-google-map-row">
              <Field label="Domain">
                <input
                  className="op-field"
                  value={row.target}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAhrefsTargets((rows) =>
                      rows.map((r, i) => (i === idx ? { ...r, target: v } : r)),
                    );
                  }}
                  placeholder="klinky.io"
                  autoComplete="off"
                />
              </Field>
              <Field label="Product">
                <select
                  className="op-field"
                  value={row.productId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAhrefsTargets((rows) =>
                      rows.map((r, i) =>
                        i === idx ? { ...r, productId: v } : r,
                      ),
                    );
                  }}
                >
                  {props.products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>
              {ahrefsTargets.length > 1 ? (
                <button
                  type="button"
                  className="op-btn ghost"
                  onClick={() =>
                    setAhrefsTargets((rows) =>
                      rows.filter((_, i) => i !== idx),
                    )
                  }
                >
                  Remove
                </button>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderPostHogForm() {
    const stored = props.integrations.find(
      (i) => i.provider === "posthog",
    )?.credentialsConfigured;
    return (
      <div className="op-google-wizard">
        <p className="op-conn-hint">
          One personal API key + project per product. Name{" "}
          <code>signupEvent</code> and <code>firstValueEvent</code> from that
          project’s catalog (see{" "}
          <code>docs/operations/posthog-event-map.md</code>). Until both events
          are set, Outpost stays connected but unmapped — no invented zeros.
        </p>
        <div className="op-google-map-block">
          <div className="op-google-map-head">
            <strong>PostHog projects</strong>
            <button
              type="button"
              className="op-btn ghost"
              onClick={() =>
                setPosthogRows((rows) => [
                  ...rows,
                  {
                    productId: props.products[0]?.id ?? "",
                    host: "https://eu.posthog.com",
                    projectId: "",
                    apiKey: "",
                    signupEvent: "",
                    firstValueEvent: "",
                  },
                ])
              }
            >
              Add project
            </button>
          </div>
          {posthogRows.map((row, idx) => (
            <div key={`ph-${idx}`} className="op-posthog-map-block">
              <Field label="Product">
                <select
                  className="op-field"
                  value={row.productId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPosthogRows((rows) =>
                      rows.map((r, i) =>
                        i === idx ? { ...r, productId: v } : r,
                      ),
                    );
                  }}
                >
                  {props.products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Host">
                <select
                  className="op-field"
                  value={row.host}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPosthogRows((rows) =>
                      rows.map((r, i) => (i === idx ? { ...r, host: v } : r)),
                    );
                  }}
                >
                  <option value="https://eu.posthog.com">
                    eu.posthog.com
                  </option>
                  <option value="https://us.posthog.com">
                    us.posthog.com
                  </option>
                </select>
              </Field>
              <Field label="Project ID">
                <input
                  className="op-field"
                  value={row.projectId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPosthogRows((rows) =>
                      rows.map((r, i) =>
                        i === idx ? { ...r, projectId: v } : r,
                      ),
                    );
                  }}
                  placeholder="255217"
                  autoComplete="off"
                />
              </Field>
              <Field
                label="Personal API key"
                hint={stored ? "Leave blank to reuse stored key" : undefined}
              >
                <input
                  className="op-field"
                  type="password"
                  value={row.apiKey}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPosthogRows((rows) =>
                      rows.map((r, i) =>
                        i === idx ? { ...r, apiKey: v } : r,
                      ),
                    );
                  }}
                  placeholder={
                    stored ? "•••• stored — paste to rotate" : "phx_…"
                  }
                  autoComplete="off"
                  spellCheck={false}
                />
              </Field>
              <Field label="Signup event">
                <input
                  className="op-field"
                  value={row.signupEvent}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPosthogRows((rows) =>
                      rows.map((r, i) =>
                        i === idx ? { ...r, signupEvent: v } : r,
                      ),
                    );
                  }}
                  placeholder="user_signed_up"
                  autoComplete="off"
                />
              </Field>
              <Field label="First-value event">
                <input
                  className="op-field"
                  value={row.firstValueEvent}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPosthogRows((rows) =>
                      rows.map((r, i) =>
                        i === idx ? { ...r, firstValueEvent: v } : r,
                      ),
                    );
                  }}
                  placeholder="first_value"
                  autoComplete="off"
                />
              </Field>
              {posthogRows.length > 1 ? (
                <button
                  type="button"
                  className="op-btn ghost"
                  onClick={() =>
                    setPosthogRows((rows) => rows.filter((_, i) => i !== idx))
                  }
                >
                  Remove
                </button>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderGoogleWizard(surface: "ga" | "gsc") {
    const rows = initialGoogleRows();
    return (
      <GoogleConnectWizard
        surface={surface}
        products={props.products}
        googleClientId={props.googleClientId}
        googleClientSecret={props.googleClientSecret}
        credentialsStored={googleSecretsStored()}
        busy={
          props.busy.startsWith("ga:") ||
          props.busy.startsWith("gsc:") ||
          props.busy.includes(":oauth")
        }
        actionError={props.actionError}
        initialProperties={rows.properties}
        initialSites={rows.sites}
        onGoogleClientId={props.onGoogleClientId}
        onGoogleClientSecret={props.onGoogleClientSecret}
        onAuthorize={props.onGoogleAuthorize}
        onSaveMappings={props.onGoogleSaveMappings}
      />
    );
  }

  function renderCredentialFields(provider: string) {
    if (provider === "buffer") {
      return (
        <Field
          label="Buffer API key"
          hint="Buffer → Settings → API → personal access token"
        >
          <input
            className="op-field"
            type="password"
            value={props.bufferApiKey}
            onChange={(e) => props.onBufferApiKey(e.target.value)}
            placeholder="bua_…"
            autoComplete="off"
          />
        </Field>
      );
    }
    if (provider === "instantly") {
      return (
        <Field
          label="Instantly API key"
          hint="Instantly → Settings → Integrations → API → create v2 key"
        >
          <input
            className="op-field"
            type="password"
            value={props.instantlyApiKey}
            onChange={(e) => props.onInstantlyApiKey(e.target.value)}
            placeholder="Instantly API v2 key"
            autoComplete="off"
          />
        </Field>
      );
    }
    if (provider === "ga" || provider === "gsc") {
      return renderGoogleWizard(provider);
    }
    if (provider === "ahrefs") {
      return renderAhrefsForm();
    }
    if (provider === "posthog") {
      return renderPostHogForm();
    }
    return (
      <p className="op-notice">This connector isn’t live in the lookout yet.</p>
    );
  }

  function canSave(provider: string): boolean {
    if (props.busy) return false;
    return missingForSave(provider).length === 0;
  }

  function renderSaveFeedback(provider: string) {
    if (provider === "ga" || provider === "gsc") return null;
    const missing = missingForSave(provider);
    return (
      <>
        {missing.length > 0 ? (
          <p className="op-notice err" role="status">
            Still need {missing.join(" · ")} before lighting this lookout.
          </p>
        ) : null}
        {props.actionError ? (
          <p className="op-notice err" role="alert">
            {props.actionError}
          </p>
        ) : null}
      </>
    );
  }

  return (
    <section className="op-panel op-conn-panel">
      <div className="op-panel-head">
        <div>
          <h2>Connections</h2>
          <small>
            {lit}/{liveTotal} lit · each platform widens the lookout
          </small>
        </div>
        <div className="op-conn-head-actions">
          <button
            ref={addBtnRef}
            type="button"
            className="op-conn-add-btn"
            onClick={openModal}
            aria-haspopup="dialog"
            aria-expanded={props.pickerOpen}
          >
            <Plus size={18} aria-hidden />
            Add connection
          </button>
        </div>
      </div>

      <div className="op-conn-progress" aria-label="Connection progress">
        <div className="op-conn-progress-track">
          <div
            className="op-conn-progress-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {props.loading ? (
        <p className="op-notice">Loading connections…</p>
      ) : null}
      {props.error ? <p className="op-notice err">{props.error}</p> : null}
      {props.notice ? <p className="op-notice">{props.notice}</p> : null}
      {!props.secretsKeyConfigured ? (
        <p className="op-notice err">
          Platform needs INTEGRATION_SECRETS_KEY before credentials can be
          stored.
        </p>
      ) : null}

      <div className="op-conn-modules">
        {active.length === 0 ? (
          <div className="op-conn-empty">
            <img
              src="/brand/outpost16-no-goals-signal-flag.png"
              alt=""
              width={72}
              height={72}
            />
            <p>No connections yet. Add one to start gathering signal.</p>
            <button
              type="button"
              className="op-conn-add-btn"
              onClick={openModal}
            >
              <Plus size={18} aria-hidden />
              Add connection
            </button>
          </div>
        ) : null}

        {active.map((ic) => {
          const meta = catalog(ic.provider);
          const open = props.tuneProvider === ic.provider;
          return (
            <article
              key={ic.provider}
              className={`op-conn-module${open ? " is-open" : ""}${ic.status === "connected" || ic.status === "stale" ? " is-lit" : ""}`}
            >
              <button
                type="button"
                className="op-conn-module-head"
                onClick={() =>
                  props.onTuneProvider(open ? null : ic.provider)
                }
                aria-expanded={open}
              >
                <div className="op-conn-module-title">
                  <ConnectorMark
                    provider={ic.provider}
                    label={ic.label}
                    size={32}
                  />
                  <div>
                    <h3>{ic.label}</h3>
                    <small>
                      {statusLabel(ic.status, ic)}
                      {ic.lastSyncedAt ? ` · synced ${ic.lastSyncedAt}` : ""}
                    </small>
                  </div>
                </div>
                <ChevronDown size={18} className="op-conn-chevron" aria-hidden />
              </button>
              {ic.provider === "buffer"
                ? (() => {
                    const stats = parseBufferStats(ic.metaJson);
                    return stats ? <BufferStatsStrip stats={stats} /> : null;
                  })()
                : null}
              {ic.provider === "instantly"
                ? (() => {
                    const stats = parseInstantlyStats(ic.metaJson);
                    return stats ? <InstantlyStatsStrip stats={stats} /> : null;
                  })()
                : null}
              {ic.provider === "ga"
                ? (() => {
                    const ga = parseGaMeta(ic.metaJson);
                    if (!ga || ga.sessions7d == null) return null;
                    return (
                      <div className="op-buffer-stats">
                        <div className="op-buffer-stat-row" aria-label="GA sessions">
                          <div className="op-buffer-stat">
                            <strong>{ga.sessions7d}</strong>
                            <span>Sessions 7d</span>
                          </div>
                          <div className="op-buffer-stat">
                            <strong>{ga.properties.length}</strong>
                            <span>Properties</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()
                : null}
              {ic.provider === "gsc"
                ? (() => {
                    const gsc = parseGscMeta(ic.metaJson);
                    if (!gsc) return null;
                    if (ic.status === "error" && gsc.error) {
                      return (
                        <p className="op-notice err" role="status">
                          {gsc.error}
                        </p>
                      );
                    }
                    if (gsc.needsSite && gsc.sites.length === 0) {
                      return (
                        <p className="op-conn-hint">
                          OAuth ready — add Search Console site URLs and map them
                          to products.
                        </p>
                      );
                    }
                    if (gsc.clicks7d == null && gsc.impressions7d == null) {
                      return null;
                    }
                    return (
                      <div className="op-buffer-stats">
                        <div className="op-buffer-stat-row" aria-label="GSC search">
                          <div className="op-buffer-stat">
                            <strong>{gsc.clicks7d ?? "—"}</strong>
                            <span>Clicks 7d</span>
                          </div>
                          <div className="op-buffer-stat">
                            <strong>{gsc.impressions7d ?? "—"}</strong>
                            <span>Impressions 7d</span>
                          </div>
                          <div className="op-buffer-stat">
                            <strong>{gsc.sites.length}</strong>
                            <span>Sites</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()
                : null}
              {ic.provider === "ahrefs"
                ? (() => {
                    const ah = parseAhrefsMeta(ic.metaJson);
                    if (!ah) return null;
                    const primary = ah.targets[0];
                    const fmtDelta = (n: number | null | undefined) => {
                      if (n === null || n === undefined) return "—";
                      const sign = n > 0 ? "+" : "";
                      return `${sign}${n}`;
                    };
                    const publicOnly = ah.apiMode === "public";
                    return (
                      <div className="op-buffer-stats">
                        <div
                          className="op-buffer-stat-row"
                          aria-label="Ahrefs authority"
                        >
                          <div className="op-buffer-stat">
                            <strong>{ah.domainRatingAvg ?? "—"}</strong>
                            <span>DR avg</span>
                          </div>
                          <div className="op-buffer-stat">
                            <strong>
                              {fmtDelta(
                                primary?.drDeltaSinceBaseline ??
                                  primary?.drDelta30d,
                              )}
                            </strong>
                            <span>
                              {primary?.drDeltaSinceBaseline != null
                                ? "Since baseline"
                                : "DR change"}
                            </span>
                          </div>
                          {!publicOnly ? (
                            <>
                              <div className="op-buffer-stat">
                                <strong>
                                  {ah.liveRefdomainsTotal ?? "—"}
                                </strong>
                                <span>Ref. domains</span>
                              </div>
                              <div className="op-buffer-stat">
                                <strong>{ah.orgTrafficTotal ?? "—"}</strong>
                                <span>Org traffic</span>
                              </div>
                            </>
                          ) : null}
                          <div className="op-buffer-stat">
                            <strong>{ah.targets.length}</strong>
                            <span>Domains</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()
                : null}
              {ic.provider === "posthog"
                ? (() => {
                    const ph = parsePostHogMeta(ic.metaJson);
                    if (!ph) return null;
                    return (
                      <div className="op-buffer-stats">
                        <div
                          className="op-buffer-stat-row"
                          aria-label="PostHog activation"
                        >
                          <div className="op-buffer-stat">
                            <strong>{ph.signupCount7dTotal ?? "—"}</strong>
                            <span>Signups 7d</span>
                          </div>
                          <div className="op-buffer-stat">
                            <strong>{ph.firstValueCount7dTotal ?? "—"}</strong>
                            <span>First value 7d</span>
                          </div>
                          <div className="op-buffer-stat">
                            <strong>{ph.exceptionCount7dTotal ?? "—"}</strong>
                            <span>Exceptions 7d</span>
                          </div>
                          <div className="op-buffer-stat">
                            <strong>{ph.openErrorIssuesTotal ?? "—"}</strong>
                            <span>Open errors</span>
                          </div>
                          <div className="op-buffer-stat">
                            <strong>{ph.projects.length}</strong>
                            <span>Projects</span>
                          </div>
                        </div>
                        {ph.unmappedCount > 0 ? (
                          <p className="op-conn-hint">
                            {ph.unmappedCount} project
                            {ph.unmappedCount === 1 ? "" : "s"} connected but
                            unmapped (set signup + first-value events).
                          </p>
                        ) : null}
                      </div>
                    );
                  })()
                : null}
              {open ? (
                <div className="op-conn-module-body">
                  {ic.provider === "ga" ||
                  ic.provider === "gsc" ||
                  ic.provider === "ahrefs" || ic.provider === "posthog"
                    ? null
                    : (
                    <p className="op-conn-hint">
                      {meta?.blurb ?? "Update credentials or sync."}
                    </p>
                  )}
                  {renderCredentialFields(ic.provider)}
                  {renderSaveFeedback(ic.provider)}
                  {ic.provider === "ga" || ic.provider === "gsc" ? (
                    <div className="op-item-actions">
                      {(ic.status === "connected" ||
                        ic.status === "stale") && (
                        <button
                          type="button"
                          className="op-btn ghost"
                          disabled={!!props.busy}
                          onClick={() => props.onAction(ic.provider, "sync")}
                        >
                          {props.busy === `${ic.provider}:sync`
                            ? "Syncing…"
                            : "Sync"}
                        </button>
                      )}
                      <button
                        type="button"
                        className="op-btn ghost"
                        disabled={!!props.busy}
                        onClick={() =>
                          props.onAction(ic.provider, "disconnect")
                        }
                      >
                        Remove
                      </button>
                    </div>
                  ) : ic.provider === "ahrefs" ? (
                    <div className="op-item-actions">
                      <button
                        type="button"
                        className="op-primary"
                        disabled={!canSave("ahrefs")}
                        onClick={() =>
                          props.onAhrefsSave({
                            targets: ahrefsTargets
                              .filter((t) => t.target.trim() && t.productId)
                              .map((t) => ({
                                target: t.target.trim(),
                                productId: t.productId,
                              })),
                          })
                        }
                      >
                        {props.busy === "ahrefs:connect"
                          ? "Pulling Ahrefs…"
                          : "Save & pull DR"}
                      </button>
                      {(ic.status === "connected" ||
                        ic.status === "stale") && (
                        <button
                          type="button"
                          className="op-btn ghost"
                          disabled={!!props.busy}
                          onClick={() => props.onAction("ahrefs", "sync")}
                        >
                          {props.busy === "ahrefs:sync" ? "Syncing…" : "Sync"}
                        </button>
                      )}
                      <button
                        type="button"
                        className="op-btn ghost"
                        disabled={!!props.busy}
                        onClick={() => props.onAction("ahrefs", "disconnect")}
                      >
                        Remove
                      </button>
                    </div>
                  ) : ic.provider === "posthog" ? (
                    <div className="op-item-actions">
                      <button
                        type="button"
                        className="op-primary"
                        disabled={!canSave("posthog")}
                        onClick={() =>
                          props.onPostHogSave({
                            projects: posthogRows
                              .filter((r) => r.productId && r.projectId.trim())
                              .map((r) => ({
                                productId: r.productId,
                                host: r.host,
                                projectId: r.projectId.trim(),
                                apiKey: r.apiKey.trim() || undefined,
                                signupEvent: r.signupEvent.trim(),
                                firstValueEvent: r.firstValueEvent.trim(),
                              })),
                          })
                        }
                      >
                        {props.busy === "posthog:connect"
                          ? "Syncing PostHog…"
                          : "Save & sync"}
                      </button>
                      {(ic.status === "connected" ||
                        ic.status === "stale" ||
                        ic.status === "error") && (
                        <button
                          type="button"
                          className="op-btn ghost"
                          disabled={!!props.busy}
                          onClick={() =>
                            props.onAction("posthog", "sync", {
                              projects: posthogRows
                                .filter((r) => r.productId && r.projectId.trim())
                                .map((r) => ({
                                  productId: r.productId,
                                  host: r.host,
                                  projectId: r.projectId.trim(),
                                  apiKey: r.apiKey.trim() || undefined,
                                  signupEvent: r.signupEvent.trim(),
                                  firstValueEvent: r.firstValueEvent.trim(),
                                })),
                            })
                          }
                        >
                          {props.busy === "posthog:sync" ? "Syncing…" : "Sync"}
                        </button>
                      )}
                      <button
                        type="button"
                        className="op-btn ghost"
                        disabled={!!props.busy}
                        onClick={() => props.onAction("posthog", "disconnect")}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="op-item-actions">
                      <button
                        type="button"
                        className="op-primary"
                        disabled={!canSave(ic.provider)}
                        onClick={() => props.onAction(ic.provider, "connect")}
                      >
                        {props.busy === `${ic.provider}:connect`
                          ? "Testing…"
                          : "Save & test"}
                      </button>
                      {(ic.status === "connected" ||
                        ic.status === "stale") && (
                        <button
                          type="button"
                          className="op-btn ghost"
                          disabled={!!props.busy}
                          onClick={() => props.onAction(ic.provider, "sync")}
                        >
                          {props.busy === `${ic.provider}:sync`
                            ? "Syncing…"
                            : "Sync"}
                        </button>
                      )}
                      <button
                        type="button"
                        className="op-btn ghost"
                        disabled={!!props.busy}
                        onClick={() =>
                          props.onAction(ic.provider, "disconnect")
                        }
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              ) : null}
            </article>
          );
        })}
      </div>

      {props.pickerOpen ? (
        <div className="op-conn-modal" role="presentation">
          <button
            type="button"
            className="op-conn-modal-backdrop"
            aria-label="Close connection gallery"
            onClick={closeModal}
          />
          <div
            ref={modalRef}
            className="op-conn-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby={modalTitleId}
          >
            <div className="op-conn-modal-head">
              <div>
                {props.draftProvider ? (
                  <button
                    type="button"
                    className="op-btn ghost op-conn-modal-back"
                    onClick={() => props.onDraftProvider(null)}
                  >
                    ← Gallery
                  </button>
                ) : null}
                <h2 id={modalTitleId}>
                  {props.draftProvider
                    ? `Configure ${catalog(props.draftProvider)?.label ?? props.draftProvider}`
                    : "Add a connection"}
                </h2>
                <small>
                  {props.draftProvider
                    ? (catalog(props.draftProvider)?.xpHint ??
                      "Paste credentials, then light the lookout.")
                    : "Pick a platform to configure. Logos mark each lookout source."}
                </small>
              </div>
              <button
                type="button"
                className="op-btn ghost op-conn-modal-close"
                aria-label="Close"
                onClick={closeModal}
              >
                <X size={18} aria-hidden />
              </button>
            </div>

            {!props.draftProvider ? (
              <div
                className="op-conn-gallery"
                role="listbox"
                aria-label="Available platforms"
              >
                {CONNECTION_CATALOG.map((c) => {
                  const already = activeProviders.has(c.provider);
                  const disabled = already || !c.live;
                  return (
                    <button
                      key={c.provider}
                      type="button"
                      className={`op-conn-gallery-tile${c.live ? "" : " is-soon"}${already ? " is-added" : ""}`}
                      role="option"
                      aria-selected={false}
                      disabled={disabled}
                      title={
                        already
                          ? "Already on your lookout"
                          : c.live
                            ? c.blurb
                            : "Coming soon"
                      }
                      onClick={() => {
                        if (disabled) return;
                        props.onDraftProvider(c.provider);
                        props.onTuneProvider(null);
                      }}
                    >
                      <span className="op-conn-gallery-logo">
                        <ConnectorMark
                          provider={c.provider}
                          label={c.label}
                          size={40}
                        />
                      </span>
                      <strong>{c.label}</strong>
                      <small>{c.live ? c.blurb : "Coming soon"}</small>
                      <em>
                        {already
                          ? "Already lit"
                          : c.live
                            ? "Configure →"
                            : "Soon"}
                      </em>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="op-conn-modal-config">
                <div className="op-conn-modal-config-lead">
                  <ConnectorMark
                    provider={props.draftProvider}
                    label={
                      catalog(props.draftProvider)?.label ?? props.draftProvider
                    }
                    size={36}
                  />
                  <p className="op-conn-hint">
                    {catalog(props.draftProvider)?.blurb}
                  </p>
                </div>
                {renderCredentialFields(props.draftProvider)}
                {renderSaveFeedback(props.draftProvider)}
                {props.draftProvider === "ga" ||
                props.draftProvider === "gsc" ? (
                  <div className="op-item-actions">
                    <button
                      type="button"
                      className="op-btn ghost"
                      onClick={() => props.onDraftProvider(null)}
                    >
                      Back
                    </button>
                  </div>
                ) : props.draftProvider === "ahrefs" ? (
                  <div className="op-item-actions">
                    <button
                      type="button"
                      className="op-primary"
                      disabled={!canSave("ahrefs")}
                      title={
                        canSave("ahrefs")
                          ? undefined
                          : `Still need ${missingForSave("ahrefs").join(", ")}`
                      }
                      onClick={() =>
                        props.onAhrefsSave({
                          targets: ahrefsTargets
                            .filter((t) => t.target.trim() && t.productId)
                            .map((t) => ({
                              target: t.target.trim(),
                              productId: t.productId,
                            })),
                        })
                      }
                    >
                      {props.busy === "ahrefs:connect"
                        ? "Pulling Ahrefs…"
                        : "Save & pull DR"}
                    </button>
                    <button
                      type="button"
                      className="op-btn ghost"
                      onClick={() => props.onDraftProvider(null)}
                    >
                      Back
                    </button>
                  </div>
                ) : props.draftProvider === "posthog" ? (
                  <div className="op-item-actions">
                    <button
                      type="button"
                      className="op-primary"
                      disabled={!canSave("posthog")}
                      title={
                        canSave("posthog")
                          ? undefined
                          : `Still need ${missingForSave("posthog").join(", ")}`
                      }
                      onClick={() =>
                        props.onPostHogSave({
                          projects: posthogRows
                            .filter((r) => r.productId && r.projectId.trim())
                            .map((r) => ({
                              productId: r.productId,
                              host: r.host,
                              projectId: r.projectId.trim(),
                              apiKey: r.apiKey.trim() || undefined,
                              signupEvent: r.signupEvent.trim(),
                              firstValueEvent: r.firstValueEvent.trim(),
                            })),
                        })
                      }
                    >
                      {props.busy === "posthog:connect"
                        ? "Syncing PostHog…"
                        : "Save & sync"}
                    </button>
                    <button
                      type="button"
                      className="op-btn ghost"
                      onClick={() => props.onDraftProvider(null)}
                    >
                      Back
                    </button>
                  </div>
                ) : (
                  <div className="op-item-actions">
                    <button
                      type="button"
                      className="op-primary"
                      disabled={!canSave(props.draftProvider)}
                      title={
                        canSave(props.draftProvider)
                          ? undefined
                          : `Still need ${missingForSave(props.draftProvider).join(", ")}`
                      }
                      onClick={() =>
                        props.onAction(props.draftProvider!, "connect")
                      }
                    >
                      {props.busy === `${props.draftProvider}:connect`
                        ? "Lighting…"
                        : "Save & light lookout"}
                    </button>
                    <button
                      type="button"
                      className="op-btn ghost"
                      onClick={() => props.onDraftProvider(null)}
                    >
                      Back
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
